import { motion } from "framer-motion";
import { Github, Linkedin } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/hooks/useLanguage";
import { BackgroundMedia } from "./BackgroundMedia";
import { HeroEmailForm } from "./HeroEmailForm";

const GITHUB_URL = "https://github.com/mohamed2394";
const LINKEDIN_URL = "https://www.linkedin.com/in/mohamed-medjabri-b173531b6/";

const socials = [
  { label: "GitHub", href: GITHUB_URL, icon: Github },
  { label: "LinkedIn", href: LINKEDIN_URL, icon: Linkedin },
];

const container = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.12, delayChildren: 0.15 },
  },
};

const item = {
  hidden: { opacity: 0, y: 24 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] as const },
  },
};

export function Hero() {
  const { t } = useLanguage();

  return (
    <section className="relative flex h-dvh items-center overflow-hidden">
      <BackgroundMedia />

      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="mx-auto w-full max-w-5xl px-6 text-start"
      >
        <motion.h1
          variants={item}
          className={cn(
            "text-5xl leading-[0.95] text-balance sm:text-7xl lg:text-8xl",
            "font-pixel font-bold tracking-tight",
            "dark:[text-shadow:0_2px_24px_rgba(0,0,0,0.65)]"
          )}
        >
          Medjabri
          <br />
          <span className="text-foreground/60 dark:text-foreground/70">
            Mohamed Amine
          </span>
        </motion.h1>

        <motion.p
          variants={item}
          className="mt-8 max-w-xl text-xl font-medium text-foreground sm:text-2xl dark:[text-shadow:0_1px_16px_rgba(0,0,0,0.6)]"
        >
          {t.hero.role}
        </motion.p>

        <motion.p
          variants={item}
          className="mt-3 max-w-xl text-base leading-relaxed text-foreground/80 sm:text-lg dark:[text-shadow:0_1px_14px_rgba(0,0,0,0.55)]"
        >
          {t.hero.tagline}
        </motion.p>

        <motion.div
          variants={item}
          className="mt-10 flex flex-col items-start gap-5"
        >
          <HeroEmailForm />

          <div className="flex items-center gap-1" dir="ltr">
            {socials.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                className={cn(
                  "flex h-10 w-10 items-center justify-center rounded-full",
                  "text-muted-foreground transition-colors duration-200 hover:bg-secondary hover:text-foreground"
                )}
              >
                <s.icon className="h-5 w-5" />
              </a>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
